"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";

import { Logomark } from "@/components/ui/Logomark";

// Three.js + OrbitControls + RoomEnvironment + SVGLoader are ~165 KB gzipped, so the 3D logo never
// competes with the page itself:
//   1. `next/dynamic` with `ssr: false` keeps the chunk out of the server render and the first load.
//   2. Nothing is fetched until the page has finished loading and the visitor has been on it for
//      3 seconds, and the footer is within a screen of the viewport.
//   3. 2G or data-saving connections, reduced motion and browsers without WebGL keep the static
//      <Logomark>, which holds the same box so the swap never shifts the layout.
const RefDocsLogoThree = dynamic(() => import("@/components/RefDocsLogoThree"), {
  ssr: false,
  loading: () => null,
});

const DELAY_AFTER_LOAD_MS = 3000;

type LazyFooterLogoProps = {
  glowIntensity?: number;
  lightIntensity?: number;
  purpleDepth?: number;
  reflectionIntensity?: number;
  alwaysOscillate?: boolean;
  className?: string;
  innerClassName?: string;
  canvasClassName?: string;
};

type NetworkInformation = { saveData?: boolean; effectiveType?: string };

function canShow3d(): boolean {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  // Touch screens keep the static mark: the logo's charm is following a mouse, and on a phone CPU
  // the scene's setup blocked the page for over a second (6 s on Lighthouse's throttled phone).
  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return false;
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  // Not "3g": Chrome gives that to any connection with a round trip over ~270 ms, whatever its
  // bandwidth (a desktop on ordinary Wi-Fi or a VPN gets it), and the chunk only loads once the page is done.
  if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType ?? "")) return false;
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") ?? canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

export default function LazyFooterLogo(props: LazyFooterLogoProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [mount, setMount] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !canShow3d()) return;

    let waited = false;
    let near = typeof IntersectionObserver === "undefined";
    const tryMount = () => {
      if (waited && near) setMount(true);
    };

    let timer: number | undefined;
    const startTimer = () => {
      timer = window.setTimeout(() => {
        waited = true;
        tryMount();
      }, DELAY_AFTER_LOAD_MS);
    };
    if (document.readyState === "complete") startTimer();
    else window.addEventListener("load", startTimer, { once: true });

    const observer =
      typeof IntersectionObserver === "undefined"
        ? undefined
        : new IntersectionObserver(
            (entries) => {
              near = entries.some((e) => e.isIntersecting);
              tryMount();
            },
            { rootMargin: "100% 0px" }
          );
    observer?.observe(el);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("load", startTimer);
      observer?.disconnect();
    };
  }, []);

  return (
    <div ref={sentinelRef} className={props.className}>
      {mount ? (
        <RefDocsLogoThree {...props} />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <Logomark className="h-3/4 w-3/4" />
        </div>
      )}
    </div>
  );
}
